import { fmtDateTime } from '@/lib/dates.js';
import { useState, useMemo, useEffect, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Info, KeyRound, Loader2, ShieldCheck, Trash2 } from 'lucide-react';
import {
 phi, type PhiGrantRow,
  studies, adminUsers,
} from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { MultiSelectSearch, type MultiSelectOption } from '@/components/ui/MultiSelectSearch';
import { titleCase } from '@/lib/humanise';
import { toast } from 'sonner';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { UnsavedChangesModal } from '@/components/ui/UnsavedChangesModal';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';


const PHI_FIELD_KEYS = [
  'full_name', 'date_of_birth', 'address_street', 'address_city',
  'address_state', 'address_zip', 'phone_e164', 'email_personal', 'mrn', 'ssn_last4',
] as const;

// Human labels for the field-key checkboxes — replaces the raw snake_case
// chips the user flagged.
const PHI_FIELD_LABELS: Record<string, string> = {
  full_name: 'Full name',
  date_of_birth: 'Date of birth',
  address_street: 'Street address',
  address_city: 'City',
  address_state: 'State',
  address_zip: 'ZIP',
  phone_e164: 'Phone',
  email_personal: 'Personal email',
  mrn: 'Medical record number',
  ssn_last4: 'SSN (last 4)',
};

const GRANT_ROLES = new Set(['platform_admin', 'application_admin', 'compliance', 'site_admin', 'principal_investigator']);

export function RemoteViewTab() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canGrant = !!user && GRANT_ROLES.has(user.role);

  // Filters above the active-grants table — multi-select, searchable, name-based.
  const [filterMonitors, setFilterMonitors] = useState<string[]>([]);
  const [filterStudies, setFilterStudies] = useState<string[]>([]);
  const [filterSites, setFilterSites] = useState<string[]>([]);

  // Grant form — single-select dropdowns for the same entities.
  const [form, setForm] = useState<{ monitor_user_id: string; study_id: string; site_id: string; field_keys: string[] }>({
    monitor_user_id: '',
    study_id: '',
    site_id: user?.activeSiteId ?? '',
    field_keys: [],
  });
  const [error, setError] = useState<string | null>(null);

  // Phase 2 Defect 2.5 sibling sweep — the grant form assembles a
  // multi-select of PHI field keys plus target study/site/monitor.
  // Navigating away without hitting "Grant access" silently discards
  // the selections. Consider the form dirty whenever any of the four
  // discriminants has been touched.
  const formDirty =
    !!form.monitor_user_id ||
    !!form.study_id ||
    form.field_keys.length > 0 ||
    (form.site_id !== (user?.activeSiteId ?? ''));
  const unsavedGuard = useUnsavedChangesGuard(canGrant && formDirty);

  // Lookup data for dropdowns + name resolution in the grants table.
  const usersQ = useQuery({ queryKey: ['admin-users'], queryFn: () => adminUsers.list() });
  const studiesQ = useQuery({ queryKey: ['studies'], queryFn: () => studies.list() });

  const usersById = useMemo(() => {
    const m = new Map<string, { full_name: string; email: string }>();
    usersQ.data?.rows.forEach((u) => m.set(u.user_id, { full_name: u.full_name, email: u.email }));
    return m;
  }, [usersQ.data]);
  const studiesById = useMemo(() => {
    const m = new Map<string, { title: string; protocol: string }>();
    studiesQ.data?.rows.forEach((s) => m.set(s.study_id, { title: s.title, protocol: s.protocol_number }));
    return m;
  }, [studiesQ.data]);
  const sitesById = useMemo(() => {
    const m = new Map<string, string>();
    if (user?.siteNames) for (const [id, name] of Object.entries(user.siteNames)) m.set(id, name);
    return m;
  }, [user?.siteNames]);

  const siteOptions = useMemo<MultiSelectOption[]>(
    () => Array.from(sitesById.entries()).map(([id, name]) => ({ value: id, label: name })),
    [sitesById],
  );

  // ── Filter-row cascade ──────────────────────────────────────────────────
  // Site selection narrows the available Study and Monitor options.
  // No site selected → show every study / every monitor.
  const studyOptionsForFilter = useMemo<MultiSelectOption[]>(() => {
    const allowedSites = filterSites.length > 0 ? new Set(filterSites) : null;
    return (studiesQ.data?.rows ?? [])
      .filter((s) => !allowedSites || allowedSites.has(s.site_id))
      .map((s) => ({ value: s.study_id, label: s.title, sublabel: s.protocol_number }));
  }, [studiesQ.data, filterSites]);

  const monitorOptionsForFilter = useMemo<MultiSelectOption[]>(() => {
    const allowedSites = filterSites.length > 0 ? new Set(filterSites) : null;
    return (usersQ.data?.rows ?? [])
      .filter((u) => u.sites.some((s) => s.role === 'monitor' && (!allowedSites || allowedSites.has(s.site_id))))
      .map((u) => ({ value: u.user_id, label: u.full_name || u.email.split('@')[0] || u.email, sublabel: u.email }));
  }, [usersQ.data, filterSites]);

  // Drop orphaned child selections when the parent (Site) changes.
  useEffect(() => {
    const validStudies = new Set(studyOptionsForFilter.map((o) => o.value));
    const cleanedStudies = filterStudies.filter((id) => validStudies.has(id));
    if (cleanedStudies.length !== filterStudies.length) setFilterStudies(cleanedStudies);

    const validMonitors = new Set(monitorOptionsForFilter.map((o) => o.value));
    const cleanedMonitors = filterMonitors.filter((id) => validMonitors.has(id));
    if (cleanedMonitors.length !== filterMonitors.length) setFilterMonitors(cleanedMonitors);
    // intentionally only react to the parent's option list shrinking, not to
    // the child selections themselves (which we set inside the effect)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyOptionsForFilter, monitorOptionsForFilter]);

  // ── Grant-form cascade ──────────────────────────────────────────────────
  // Same idea for the single-select grant form: pick a site first, study and
  // monitor narrow to that site.
  const studyOptionsForForm = useMemo<MultiSelectOption[]>(() => {
    if (!form.site_id) return [];
    return (studiesQ.data?.rows ?? [])
      .filter((s) => s.site_id === form.site_id)
      .map((s) => ({ value: s.study_id, label: s.title, sublabel: s.protocol_number }));
  }, [studiesQ.data, form.site_id]);

  const monitorOptionsForForm = useMemo<MultiSelectOption[]>(() => {
    if (!form.site_id) return [];
    return (usersQ.data?.rows ?? [])
      .filter((u) => u.sites.some((s) => s.role === 'monitor' && s.site_id === form.site_id))
      .map((u) => ({ value: u.user_id, label: u.full_name || u.email.split('@')[0] || u.email, sublabel: u.email }));
  }, [usersQ.data, form.site_id]);

  // Clear the form's study / monitor if the chosen site no longer permits them.
  useEffect(() => {
    setForm((f) => {
      const studyOk = studyOptionsForForm.some((o) => o.value === f.study_id);
      const monitorOk = monitorOptionsForForm.some((o) => o.value === f.monitor_user_id);
      if (studyOk && monitorOk) return f;
      return {
        ...f,
        study_id: studyOk ? f.study_id : '',
        monitor_user_id: monitorOk ? f.monitor_user_id : '',
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.site_id, studyOptionsForForm, monitorOptionsForForm]);

  // Backend supports one-of (study_id | monitor_user_id | site_id). Pick the
  // most-restrictive filter the user has set; the remaining selected values
  // narrow further client-side via `filteredRows`. With NO filters the backend
  // falls back to a role-scoped default (platform=every grant, site_admin/PI
  // =every grant at their site, monitor=their own).
  const grantsFilter = useMemo(() => {
    if (filterStudies[0]) return { study_id: filterStudies[0] };
    if (filterSites[0]) return { site_id: filterSites[0] };
    if (filterMonitors[0]) return { monitor_user_id: filterMonitors[0] };
    return {};
  }, [filterStudies, filterSites, filterMonitors]);

  const grants = useQuery({
    queryKey: ['phi-grants', grantsFilter],
    queryFn: () => phi.listGrants(grantsFilter),
    enabled: !!user,
  });

  const filteredRows = useMemo(() => {
    const rows = grants.data?.rows ?? [];
    return rows.filter((r) => {
      if (filterMonitors.length > 0 && !filterMonitors.includes(r.monitor_user_id)) return false;
      if (filterSites.length > 0 && !filterSites.includes(r.site_id)) return false;
      if (filterStudies.length > 1 && !filterStudies.includes(r.study_id)) return false;
      return true;
    });
  }, [grants.data, filterMonitors, filterSites, filterStudies]);

  const create = useMutation({
    mutationFn: () => phi.grant(form),
    onSuccess: () => {
      const monitorName = usersById.get(form.monitor_user_id)?.full_name
        ?? usersById.get(form.monitor_user_id)?.email
        ?? 'monitor';
      const studyName = studiesById.get(form.study_id)?.title ?? 'study';
      toast.success(`PHI access granted to ${monitorName} for ${studyName}`);
      setForm((f) => ({ ...f, field_keys: [] }));
      setError(null);
      qc.invalidateQueries({ queryKey: ['phi-grants'] });
    },
    onError: (err) => {
      const msg = errorText(err, 'Failed to create grant');
      setError(msg);
      toast.error(msg);
    },
  });

  const revoke = useMutation({
    mutationFn: (grant_id: string) => phi.revoke(grant_id),
    onSuccess: () => {
      toast.success('PHI grant revoked');
      qc.invalidateQueries({ queryKey: ['phi-grants'] });
    },
    onError: (err) => toast.error(errorText(err, 'Failed to revoke grant')),
  });

  function handleGrant(e: FormEvent) {
    e.preventDefault();
    if (!form.monitor_user_id || !form.study_id || !form.site_id || form.field_keys.length === 0) return;
    create.mutate();
  }

  function toggleField(k: string) {
    setForm((f) => ({
      ...f,
      field_keys: f.field_keys.includes(k) ? f.field_keys.filter((x) => x !== k) : [...f.field_keys, k],
    }));
  }

  function clearFilters() { setFilterMonitors([]); setFilterStudies([]); setFilterSites([]); }

  const displayUser = (id: string) => {
    const u = usersById.get(id);
    return u ? (u.full_name || u.email) : 'Unknown user';
  };
  const displayStudy = (id: string) => studiesById.get(id)?.title ?? 'Unknown study';
  const displaySite = (id: string) => sitesById.get(id) ?? 'Unknown site';

  return (
    <div className="space-y-4">
      <div className="card p-4">
        <div className="mb-3 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-brand-500" />
          <h2 className="text-sm font-semibold text-ink-primary">Active PHI grants</h2>
        </div>
        <div className="grid gap-3 md:grid-cols-3 mb-3">
          <div>
            <label className="label text-xs">Site</label>
            <MultiSelectSearch
              options={siteOptions} value={filterSites} onChange={setFilterSites}
              placeholder="Any site"
            />
          </div>
          <div>
            <label className="label text-xs">
              Study
              {filterSites.length > 0 && (
                <span className="ml-1 text-[10px] text-ink-muted">(filtered by site)</span>
              )}
            </label>
            <MultiSelectSearch
              options={studyOptionsForFilter} value={filterStudies} onChange={setFilterStudies}
              placeholder={filterSites.length > 0 ? 'Any study at this site' : 'Any study'}
            />
          </div>
          <div>
            <label className="label text-xs">
              Monitor
              {filterSites.length > 0 && (
                <span className="ml-1 text-[10px] text-ink-muted">(filtered by site)</span>
              )}
            </label>
            <MultiSelectSearch
              options={monitorOptionsForFilter} value={filterMonitors} onChange={setFilterMonitors}
              placeholder={filterSites.length > 0 ? 'Any monitor at this site' : 'Any monitor'}
            />
          </div>
        </div>
        {(filterMonitors.length + filterStudies.length + filterSites.length) > 0 && (
          <button type="button" className="btn-ghost text-xs mb-2" onClick={clearFilters}>
            Clear filters
          </button>
        )}

        {grants.isLoading && (
          <div className="flex items-center justify-center gap-2 py-6 text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {grants.isError && (
          <div className={`flex items-start gap-2 p-3 text-sm ${errorToneClass(grants.error)}`}>
            {isPermissionError(grants.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
            <span>{errorText(grants.error, 'Failed to load grants.')}</span>
          </div>
        )}
        {grants.data && filteredRows.length === 0 && (
          <div className="py-6 text-center text-sm text-ink-muted">No active grants.</div>
        )}
        {filteredRows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-surface-border bg-surface-muted text-left text-xs uppercase tracking-wide text-ink-muted">
                <tr>
                  <th className="px-3 py-2">Monitor</th>
                  <th className="px-3 py-2">Study</th>
                  <th className="px-3 py-2">Site</th>
                  <th className="px-3 py-2">Unlocked fields</th>
                  <th className="px-3 py-2">Granted</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r: PhiGrantRow) => (
                  <tr key={r.grant_id} className="border-b border-surface-border last:border-0">
                    <td className="px-3 py-2 text-xs">
                      <div className="font-medium text-ink-primary">{displayUser(r.monitor_user_id)}</div>
                      {usersById.get(r.monitor_user_id)?.email && (
                        <div className="text-[10px] text-ink-muted">{usersById.get(r.monitor_user_id)!.email}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">{displayStudy(r.study_id)}</td>
                    <td className="px-3 py-2 text-xs">{displaySite(r.site_id)}</td>
                    <td className="px-3 py-2 text-xs">
                      <div className="flex flex-wrap gap-1">
                        {r.field_keys.map((k) => (
                          <span key={k} className="rounded bg-surface-muted px-1.5 py-0.5 text-[10px]">
                            {PHI_FIELD_LABELS[k] ?? titleCase(k)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs">{fmtDateTime(r.granted_at)}</td>
                    <td className="px-3 py-2 text-right">
                      {canGrant && !r.revoked_at && (
                        <button type="button" className="btn-ghost text-xs" onClick={() => revoke.mutate(r.grant_id)} disabled={revoke.isPending}>
                          <Trash2 className="h-3.5 w-3.5" /> Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {canGrant && (
        <form className="card space-y-3 p-4" onSubmit={handleGrant}>
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-brand-500" />
            <h2 className="text-sm font-semibold text-ink-primary">Grant PHI access</h2>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <div>
              <label className="label text-xs" htmlFor="g-site">Site</label>
              <select
                id="g-site"
                className="input"
                required
                value={form.site_id}
                onChange={(e) => setForm({ ...form, site_id: e.target.value })}
              >
                <option value="">Select a site…</option>
                {siteOptions.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label text-xs" htmlFor="g-study">Study</label>
              <select
                id="g-study"
                className="input"
                required
                disabled={!form.site_id}
                value={form.study_id}
                onChange={(e) => setForm({ ...form, study_id: e.target.value })}
              >
                <option value="">
                  {form.site_id ? 'Select a study…' : 'Pick a site first'}
                </option>
                {studyOptionsForForm.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}{s.sublabel ? ` — ${s.sublabel}` : ''}
                  </option>
                ))}
              </select>
              {form.site_id && studyOptionsForForm.length === 0 && (
                <p className="mt-1 text-[10px] text-ink-muted">No studies at this site.</p>
              )}
            </div>
            <div>
              <label className="label text-xs" htmlFor="g-mon">Monitor</label>
              <select
                id="g-mon"
                className="input"
                required
                disabled={!form.site_id}
                value={form.monitor_user_id}
                onChange={(e) => setForm({ ...form, monitor_user_id: e.target.value })}
              >
                <option value="">
                  {form.site_id ? 'Select a monitor…' : 'Pick a site first'}
                </option>
                {monitorOptionsForForm.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}{m.sublabel ? ` (${m.sublabel})` : ''}
                  </option>
                ))}
              </select>
              {form.site_id && monitorOptionsForForm.length === 0 && (
                <p className="mt-1 text-[10px] text-ink-muted">No monitors at this site.</p>
              )}
            </div>
          </div>
          <div>
            <label className="label text-xs">Fields to unmask</label>
            <div className="flex flex-wrap gap-2">
              {PHI_FIELD_KEYS.map((k) => (
                <label key={k} className="flex items-center gap-1 rounded border border-surface-border px-2 py-1 text-xs">
                  <input type="checkbox" checked={form.field_keys.includes(k)} onChange={() => toggleField(k)} />
                  {PHI_FIELD_LABELS[k] ?? titleCase(k)}
                </label>
              ))}
            </div>
          </div>
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-danger-100 bg-danger-50 p-3 text-sm text-danger-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <button type="submit" className="btn-primary" disabled={create.isPending || form.field_keys.length === 0}>
            {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
            Grant access
          </button>
        </form>
      )}

      {/* Phase 2 Defect 2.5 sibling sweep — protect against silent
          loss of an in-progress PHI grant selection. */}
      <UnsavedChangesModal
        guard={unsavedGuard}
        title="Leave without granting access?"
        body="You have not yet granted the PHI access you were configuring. If you leave now, the monitor / study / field selections will be lost."
        proceedLabel="Discard & leave"
        stayLabel="Stay and grant"
      />
    </div>
  );
}
